import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import i18n from '@/i18n'
import { api } from '@/lib/api/client'
import { projectKeys, useInvalidateProjects } from './api'
import { columnOf, mapTask, priorityBody, taskTypeBody } from './map'
import type { CommentVisibility, Project, Task, TaskInput, TaskStatus } from './types'

/** Ekran modeli → görev panosu gövdesi (durum = teknik pano sütunu). */
function taskBody(projectId: string, input: TaskInput & { assignmentReason?: string }) {
  return {
    title: input.title,
    description: input.description,
    task_type: taskTypeBody(input.type),
    project_id: projectId,
    assignee_user_id: input.assigneeId ?? '',
    reviewer_user_id: input.reviewerId ?? '',
    parent_id: input.parentId ?? '',
    installation_id: input.installationId ?? '',
    priority: priorityBody(input.priority),
    column_key: columnOf(input.status),
    due_at: input.dueDate || '',
    start_date: input.startDate || '',
    estimated_hours: input.estimateHours,
    spent_hours: input.spentHours,
    sprint_id: input.sprintId ?? '',
    assignment_reason: input.assignmentReason ?? '',
  }
}

function useOptimisticTask<V extends { taskId: string }>(projectId: string, fn: (vars: V) => Promise<unknown>, patch: (vars: V) => Partial<Task>) {
  const qc = useQueryClient()
  const key = projectKeys.detail(projectId)
  return useMutation({
    mutationFn: fn,
    onMutate: async (vars: V) => {
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<Project>(key)
      if (previous) qc.setQueryData<Project>(key, { ...previous, tasks: previous.tasks.map((t) => (t.id === vars.taskId ? { ...t, ...patch(vars) } : t)) })
      return { previous }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous)
    },
    onSettled: () => Promise.all([qc.invalidateQueries({ queryKey: key }), qc.invalidateQueries({ queryKey: projectKeys.all, exact: true })]),
  })
}

export function useMoveTask(projectId: string) {
  return useOptimisticTask<{ taskId: string; status: TaskStatus }>(
    projectId,
    ({ taskId, status }) => api.put(`/api/v1/tasks/${taskId}`, { column_key: columnOf(status) }),
    ({ status }) => ({ status }),
  )
}

export function useAssignTask(projectId: string) {
  return useOptimisticTask<{ taskId: string; assigneeId: string | null }>(
    projectId,
    async ({ taskId, assigneeId }) => {
      const r = await api.put<Record<string, unknown>>(`/api/v1/tasks/${taskId}`, { assignee_user_id: assigneeId ?? '' })
      // Doğrudan atanamadıysa (yetki ya da etkin sprintte ek görev) talep açılır; görev onaya kadar eski sorumluda kalır.
      if (r?.assignment_request_id) toast.info(i18n.t('projects.assignments.pendingToast'))
      return r
    },
    ({ assigneeId }) => ({ assigneeId }),
  )
}

export function useSaveTask(projectId: string) {
  const invalidate = useInvalidateProjects(projectId)
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: TaskInput & { assignmentReason?: string } }) => {
      const body = taskBody(projectId, input)
      const saved = id ? await api.put<Record<string, unknown>>(`/api/v1/tasks/${id}`, body) : await api.post<Record<string, unknown>>('/api/v1/tasks', body)
      const task = mapTask({ ...body, id: id ?? saved.id, task_key: saved.task_key })
      // Atama onaya düştüyse talep kimliği döner; görev bu sırada atanmamış kalır.
      return { ...task, assignmentRequestId: String(saved.assignment_request_id ?? '') }
    },
    onSuccess: invalidate,
  })
}

/** Görev silinmez, arşivlenir (geçmiş ve bağlantılar korunur). */
export function useDeleteTask(projectId: string) {
  const invalidate = useInvalidateProjects(projectId)
  return useMutation({ mutationFn: (taskId: string) => api.post(`/api/v1/tasks/${taskId}/archive`, {}), onSuccess: invalidate })
}

export function useTaskActions(projectId: string, taskId: string) {
  const qc = useQueryClient()
  const invalidate = useInvalidateProjects(projectId)
  const m = <A,>(fn: (arg: A) => Promise<unknown>) => ({ mutationFn: fn, onSuccess: invalidate })
  const current = () => qc.getQueryData<Project>(projectKeys.detail(projectId))?.tasks.find((t) => t.id === taskId)
  const base = `/api/v1/tasks/${taskId}`
  return {
    addItem: useMutation(m((text: string) => api.post(`${base}/checklist`, { title: text.trim(), position: current()?.checklist.length ?? 0 }))),
    toggleItem: useMutation(
      m((itemId: string) => {
        const item = current()?.checklist.find((c) => c.id === itemId)
        return api.put(`${base}/checklist/${itemId}`, { is_done: !item?.done })
      }),
    ),
    deleteItem: useMutation(m((itemId: string) => api.del(`${base}/checklist/${itemId}`))),
    assignItem: useMutation(
      m(({ itemId, assigneeId, dueDate }: { itemId: string; assigneeId: string; dueDate: string }) =>
        api.put(`${base}/checklist/${itemId}`, { assignee_user_id: assigneeId, due_date: dueDate }),
      ),
    ),
    addComment: useMutation(m((arg: { body: string; visibility: CommentVisibility; mentions?: string[] }) => api.post(`${base}/comments`, arg))),
    deleteComment: useMutation(m((commentId: string) => api.del(`${base}/comments/${commentId}`))),
    addDependency: useMutation(m((dependsOn: string) => api.post(`${base}/dependencies`, { depends_on: dependsOn }))),
    removeDependency: useMutation(m((dependsOn: string) => api.del(`${base}/dependencies/${dependsOn}`))),
  }
}
